import React, { useState, useEffect } from 'react';
import { Save, RefreshCw, AlertCircle, CheckCircle } from 'lucide-react';
import { 
  AVAILABLE_BIDDERS, 
  AVAILABLE_MODULES, 
  AVAILABLE_USER_ID_MODULES,
  getAvailableModulesForUI,
  validateBidderConfig 
} from '../config/prebidOptions';

const PrebidConfig = ({ config, onUpdateConfig, isLoading }) => {
  const [formData, setFormData] = useState({
    bidders: {},
    userIdModules: {},
    modules: []
  });
  
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  // Update form data when config changes
  useEffect(() => {
    if (config) {
      // Ensure we have all available bidders in the config
      const allBidders = {};
      Object.keys(AVAILABLE_BIDDERS).forEach(bidderName => {
        allBidders[bidderName] = config.bidders?.[bidderName] || { enabled: false };
      });
      
      setFormData({
        bidders: allBidders,
        userIdModules: config.userIdModules || {},
        modules: config.modules || ['priceFloors', 'consentManagementTcf', 'currency', 'userId']
      });
    }
  }, [config]);

  const handleBidderChange = (bidderName, field, value) => {
    const updatedBidders = {
      ...formData.bidders,
      [bidderName]: {
        ...formData.bidders[bidderName],
        [field]: value
      }
    };
    
    setFormData(prev => ({
      ...prev,
      bidders: updatedBidders
    }));
    
    // Validate the bidder configuration
    const validation = validateBidderConfig(bidderName, updatedBidders[bidderName]);
    setValidationErrors(prev => ({
      ...prev,
      [bidderName]: validation.errors
    }));
  };

  const handleUserIdModuleChange = (moduleName, field, value) => {
    setFormData(prev => ({
      ...prev,
      userIdModules: {
        ...prev.userIdModules,
        [moduleName]: {
          ...prev.userIdModules[moduleName],
          [field]: value
        }
      }
    }));
  };

  const handleModuleToggle = (moduleName) => {
    setFormData(prev => ({
      ...prev,
      modules: prev.modules.includes(moduleName)
        ? prev.modules.filter(m => m !== moduleName)
        : [...prev.modules, moduleName]
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);

    try {
      const updatedConfig = {
        ...config,
        bidders: formData.bidders,
        userIdModules: formData.userIdModules,
        modules: formData.modules
      };

      await onUpdateConfig(updatedConfig);
      setMessage({ type: 'success', text: 'Prebid configuration updated successfully' });
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Failed to update prebid configuration' });
    } finally {
      setSaving(false);
    }
  };

  // Get available modules from central configuration
  const availableModules = getAvailableModulesForUI();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="animate-spin h-6 w-6 text-blue-500" />
        <span className="ml-2 text-gray-600">Loading prebid configuration...</span>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-6">Prebid.js Configuration</h2>
        
        {message && (
          <div className={`mb-4 p-4 rounded-md flex items-center gap-2 ${
            message.type === 'success' 
              ? 'bg-green-50 border border-green-200 text-green-800' 
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}>
            {message.type === 'success' ? (
              <CheckCircle size={16} />
            ) : (
              <AlertCircle size={16} />
            )}
            {message.text}
          </div>
        )}

        <div className="space-y-8">
          {/* Bidder Configuration */}
          <div>
            <h3 className="text-md font-medium text-gray-900 mb-4">Bidder Configuration</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(formData.bidders).map(([bidderName, settings]) => {
                const bidderDef = AVAILABLE_BIDDERS[bidderName];
                const hasErrors = validationErrors[bidderName] && validationErrors[bidderName].length > 0;
                
                return (
                  <div key={bidderName} className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <span className="font-medium text-gray-900">{bidderDef?.label || bidderName}</span>
                        {bidderDef?.description && (
                          <div className="text-xs text-gray-500">{bidderDef.description}</div>
                        )}
                      </div>
                      <label className="inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settings.enabled || false}
                          onChange={(e) => handleBidderChange(bidderName, 'enabled', e.target.checked)}
                          className="sr-only"
                        />
                        <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          settings.enabled ? 'bg-blue-600' : 'bg-gray-200'
                        }`}>
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            settings.enabled ? 'translate-x-6' : 'translate-x-1'
                          }`} />
                        </div>
                      </label>
                    </div>
                    
                    {settings.enabled && bidderDef && (
                      <div className="space-y-3">
                        {/* Render global required parameters only */}
                        {bidderDef.globalParams?.filter(param => bidderDef.requiredParams.includes(param)).map(param => (
                          <div key={param}>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {bidderDef.paramDescriptions[param] || param}
                              <span className="text-red-500 ml-1">*</span>
                            </label>
                            <input
                              type={bidderDef.paramTypes?.[param] === 'number' ? 'number' : 'text'}
                              value={settings[param] || ''}
                              onChange={(e) => handleBidderChange(bidderName, param, 
                                bidderDef.paramTypes?.[param] === 'number' ? parseInt(e.target.value) : e.target.value
                              )}
                              className={`w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                                hasErrors ? 'border-red-300' : 'border-gray-300'
                              }`}
                              placeholder={`Enter ${param}`}
                            />
                          </div>
                        ))}
                        
                        {/* Render global optional parameters only */}
                        {bidderDef.globalParams?.filter(param => 
                          bidderDef.optionalParams.includes(param) && param !== 'bidAdjustment'
                        ).map(param => (
                          <div key={param}>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {bidderDef.paramDescriptions[param] || param}
                            </label>
                            <input
                              type={bidderDef.paramTypes?.[param] === 'number' ? 'number' : 'text'}
                              value={settings[param] || ''}
                              onChange={(e) => handleBidderChange(bidderName, param, 
                                bidderDef.paramTypes?.[param] === 'number' ? parseInt(e.target.value) : e.target.value
                              )}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                              placeholder={`Enter ${param} (optional)`}
                            />
                          </div>
                        ))}
                        
                        {/* Show note about placement-specific parameters */}
                        {bidderDef.placementParams && bidderDef.placementParams.length > 0 && (
                          <div className="bg-blue-50 border border-blue-200 rounded p-3">
                            <div className="text-sm text-blue-700">
                              <strong>Placement-specific settings:</strong>
                              <div className="mt-1">
                                Parameters like {bidderDef.placementParams.map(p => bidderDef.paramDescriptions[p] || p).join(', ')} are configured individually for each placement in the Placements tab.
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Bid Adjustment for all bidders */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Bid Adjustment
                            <span className="text-xs text-gray-500 ml-1">(multiplier: 0.5-1.5)</span>
                          </label>
                          <input
                            type="number"
                            min="0.5"
                            max="1.5"
                            step="0.05"
                            value={settings.bidAdjustment || 1.0}
                            onChange={(e) => handleBidderChange(bidderName, 'bidAdjustment', parseFloat(e.target.value))}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                            placeholder="1.0"
                          />
                          <div className="text-xs text-gray-500 mt-1">
                            {settings.bidAdjustment ? 
                              `${Math.round((settings.bidAdjustment || 1.0) * 100)}% of original bid` : 
                              '100% (no adjustment)'
                            }
                          </div>
                        </div>
                        
                        {/* Display validation errors */}
                        {hasErrors && (
                          <div className="bg-red-50 border border-red-200 rounded p-2">
                            <div className="flex items-center gap-1 text-red-800 text-sm">
                              <AlertCircle size={14} />
                              <span className="font-medium">Configuration Issues:</span>
                            </div>
                            <ul className="mt-1 text-sm text-red-700">
                              {validationErrors[bidderName].map((error, idx) => (
                                <li key={idx} className="ml-4">• {error}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* User ID Modules */}
          <div>
            <h3 className="text-md font-medium text-gray-900 mb-4">User ID Modules</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(formData.userIdModules).map(([moduleName, settings]) => {
                const moduleDef = AVAILABLE_USER_ID_MODULES[moduleName];
                
                return (
                  <div key={moduleName} className="p-4 bg-gray-50 rounded-lg">
                    <div className="mb-3">
                      <span className="font-medium text-gray-900">
                        {moduleDef?.label || moduleName}
                      </span>
                      {moduleDef?.description && (
                        <div className="text-xs text-gray-500">{moduleDef.description}</div>
                      )}
                    </div>
                    <div className="space-y-3">
                      {/* Dynamically render parameters */}
                      {moduleDef && moduleDef.defaultConfig && Object.entries(moduleDef.defaultConfig).map(([paramName, defaultValue]) => {
                        // Create user-friendly parameter labels since User ID modules don't have paramDescriptions
                        const getParameterLabel = (param) => {
                          switch (param) {
                            case 'expiry': return 'Expiry (days)';
                            case 'pubId': return 'Publisher ID';
                            case 'partner': return 'Partner ID';
                            default: return param.charAt(0).toUpperCase() + param.slice(1);
                          }
                        };
                        
                        return (
                          <div key={paramName}>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {moduleDef.paramDescriptions?.[paramName] || getParameterLabel(paramName)}
                            </label>
                            <input
                              type={moduleDef.paramTypes?.[paramName] === 'number' || paramName.includes('Id') || paramName === 'expiry' ? 'number' : 'text'}
                              min={paramName === 'expiry' ? '1' : undefined}
                              max={paramName === 'expiry' ? '365' : undefined}
                              value={settings[paramName] || defaultValue || ''}
                              onChange={(e) => handleUserIdModuleChange(moduleName, paramName, 
                                (moduleDef.paramTypes?.[paramName] === 'number' || paramName.includes('Id') || paramName === 'expiry') ? parseInt(e.target.value) : e.target.value
                              )}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                              placeholder={`Enter ${getParameterLabel(paramName)}`}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Prebid Modules */}
          <div>
            <h3 className="text-md font-medium text-gray-900 mb-4">Prebid Modules</h3>
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="text-sm text-blue-800">
                <strong>Universal Build:</strong> All modules are included in the universal prebid.js file. 
                Bidder control is managed in the "Bidder Configuration" section above.
              </div>
            </div>
            {['Core', 'User ID'].map((category) => {
              const categoryModules = availableModules.filter(module => module.category === category);
              return (
                <div key={category} className="mb-6">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">{category} Modules</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {categoryModules.map((module) => (
                      <div key={module.id} className="p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium text-gray-900 text-sm">{module.label}</div>
                            <div className="text-xs text-gray-500">{module.description}</div>
                          </div>
                          <label className="inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.modules.includes(module.id)}
                              onChange={() => handleModuleToggle(module.id)}
                              className="sr-only"
                            />
                            <div className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                              formData.modules.includes(module.id) ? 'bg-blue-600' : 'bg-gray-200'
                            }`}>
                              <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                                formData.modules.includes(module.id) ? 'translate-x-5' : 'translate-x-1'
                              }`} />
                            </div>
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end mt-8 pt-6 border-t border-gray-200">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? (
              <>
                <RefreshCw className="animate-spin h-4 w-4" />
                Saving...
              </>
            ) : (
              <>
                <Save size={16} />
                Save Configuration
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrebidConfig;